import { CSSProperties, useEffect, useState } from 'react';
import {
  companyInformationInterfaceErrors,
  countryPhoneInfo,
  organizationTypes,
  stateMapper,
  surveyOptions,
} from '../utils/constant';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store/store';
import { SelectChangeEvent } from '@mui/material';
import { companyInformationSchema } from '../utils/validations';
import { ValidationError } from 'yup';
import { createOrganization } from '../features/user/utils';
import { companyInformationInterface } from '../utils/constant';
import { useNavigate } from 'react-router-dom';
import { useMask } from '@react-input/mask';
import { jwtDecode } from 'jwt-decode';

const commonSelectStyles = {
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: '0.5rem',
    border: '0.0625rem solid #D6D6D6',
  },
};

const inputWrapperStyles: CSSProperties = {
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
};

interface DecodedSignUpToken {
  email: string;
}

const useCompany = () => {
  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();

  const signupToken = localStorage.getItem('signupToken');

  let decodedSignUpToken: DecodedSignUpToken = {
    email: '',
  };

  if (signupToken) {
    decodedSignUpToken = jwtDecode(signupToken);
  }

  // const userEmail = useSelector((state: RootState) => state.user.signUpForm.email || state.user.signInForm.email);

  // const initialDomain = userEmail ? userEmail.split('@')[1] : decodedSignUpToken.email.split('@')[1];

  const hasCreatedOrganization = useSelector((state: RootState) => state.user.hasCreatedOrganization);

  const [companyInformation, setCompanyInformation] = useState<companyInformationInterface>({
    organizationType: '',
    customOrgType: '',
    companyName: '',
    companyPhoneNumber: '',
    countryCode: 'US',
    addressOne: '',
    addressTwo: '',
    city: '',
    state: '',
    zip: '',
    surveyAnswer: '',
    surveyAnswerDetail: '',
  });

  const [fieldStates, setFieldStates] = useState<{
    [key: string]: { currentInput: string; error: string };
  }>({
    organizationDomains: { currentInput: '', error: '' },
    usersToInvite: { currentInput: '', error: '' },
  });

  const phoneMask = countryPhoneInfo[companyInformation.countryCode].mask;

  const inputRef = useMask({
    mask: phoneMask,
    replacement: { _: /\d/ },
    showMask: true,
  });

  const [errors, setErrors] = useState<companyInformationInterfaceErrors>({
    organizationType: '',
    companyName: '',
    companyPhoneNumber: '',
    countryCode: '',
    addressOne: '',
    addressTwo: '',
    city: '',
    state: '',
    zip: '',
    surveyAnswer: '',
    bodyErrors: '',
  });

  const clearErrors = () => {
    setErrors({
      organizationType: '',
      companyName: '',
      countryCode: '',
      companyPhoneNumber: '',
      addressOne: '',
      addressTwo: '',
      city: '',
      state: '',
      zip: '',
      surveyAnswer: '',
      bodyErrors: '',
    });
  };

  const clearFields = () => {
    setCompanyInformation({
      organizationType: '',
      customOrgType: '',
      companyName: '',
      countryCode: 'US',
      companyPhoneNumber: '',
      addressOne: '',
      addressTwo: '',
      city: '',
      state: '',
      zip: '',
      surveyAnswer: '',
      surveyAnswerDetail: '',
    });
  };

  const fieldsMapper = [
    {
      label: 'What best describes your firm*',
      isRow: false,
      value: companyInformation.organizationType,
      type: 'select',
      options: organizationTypes,
      name: 'organizationType',
      id: 'organizationType',
      error: errors.organizationType,
    },
    {
      label: 'Company Name*',
      isRow: false,
      value: companyInformation.companyName,
      type: 'textfield',
      maxLength: 60,
      name: 'companyName',
      id: 'companyName',
      error: errors.companyName,
    },
    {
      label: 'Address 1*',
      isRow: false,
      value: companyInformation.addressOne,
      type: 'textfield',
      autoComplete: 'address-line1',
      name: 'addressOne',
      id: 'addressOne',
      error: errors.addressOne,
    },
    {
      label: 'Address 2',
      isRow: false,
      value: companyInformation.addressTwo,
      type: 'textfield',
      id: 'addressTwo',
      name: 'addressTwo',
      error: errors.addressTwo,
    },
    {
      label: 'City*',
      isRow: true,
      value: companyInformation.city,
      sx: { flex: 3 },
      type: 'textfield',
      id: 'city',
      name: 'city',
      autoComplete: 'address-level2',
      error: errors.city,
    },
    {
      label: 'State*',
      isRow: true,
      value: companyInformation.state,
      sx: { flex: 1 },
      type: companyInformation.countryCode === 'US' ? 'select' : 'textfield',
      options: Object.values(stateMapper),
      name: 'state',
      autoComplete: 'address-level1',
      id: 'state',
      error: errors.state,
    },
    {
      label: companyInformation.countryCode === 'CA' ? 'Postal code*' : 'ZIP*',
      isRow: true,
      value: companyInformation.zip,
      sx: { flex: 1 },
      type: 'textfield',
      autoComplete: 'postal-code',
      id: 'zip',
      name: 'zip',
      error: errors.zip,
    },
    {
      label: 'Country Code*',
      isRow: true,
      value: companyInformation.countryCode,
      options: Object.entries(countryPhoneInfo).map(([id, { code }]) => ({
        id,
        label: `${id} (${code})`,
      })),
      type: 'select',
      name: 'countryCode',
      id: 'countryCode',
      sx: { flex: 0.8 },
      error: errors.countryCode,
    },
    {
      label: 'Company Phone Number*',
      isRow: true,
      value: companyInformation.companyPhoneNumber,
      type: 'textfield',
      sx: { flex: 5 },
      id: 'companyPhoneNumber',
      placeholder: phoneMask,
      name: 'companyPhoneNumber',
      inputRef: inputRef,
      error: errors.companyPhoneNumber,
    },
  ];

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setCompanyInformation((prevCompany) => ({ ...prevCompany, [name]: value }));
  };

  const onSelect = (event: SelectChangeEvent<string>, name: string) => {
    const { value } = event.target;
    if (name === 'surveyAnswer') {
      setCompanyInformation((prevCompany) => ({ ...prevCompany, [name]: value, surveyAnswerDetail: '' }));
    } else if (name === 'countryCode' && value === 'US') {
      setCompanyInformation((prevCompany) => ({
        ...prevCompany,
        [name]: value,
        state: '',
      }));
    } else {
      setCompanyInformation((prevCompany) => ({
        ...prevCompany,
        [name]: value,
      }));
    }
  };

  const onRegisterOrganization = async () => {
    try {
      await companyInformationSchema.validate(companyInformation, {
        abortEarly: false,
      });

      clearErrors();
      await dispatch(createOrganization(companyInformation)).then((result) => {
        if (createOrganization.fulfilled.match(result)) {
          clearFields();
        }
      });
    } catch (err: unknown) {
      if (err instanceof ValidationError) {
        const validationErrors: companyInformationInterfaceErrors = {
          organizationType: '',
          companyName: '',
          countryCode: '',
          companyPhoneNumber: '',
          addressOne: '',
          addressTwo: '',
          city: '',
          state: '',
          zip: '',
          surveyAnswer: '',
          bodyErrors: '',
        };

        err.inner.forEach((error) => {
          const path = error.path || '';
          if (path && validationErrors.hasOwnProperty(path)) {
            validationErrors[path as keyof companyInformationInterfaceErrors] = error.message;
          }
        });
        setErrors(validationErrors);
      } else {
        console.error('Unexpected error:', err);
      }
    }
  };

  const onSurveyAnswerDetailChange = (e: any) => {
    setCompanyInformation((prev) => ({ ...prev, surveyAnswerDetail: e.target.value }));
  };

  useEffect(() => {
    if (hasCreatedOrganization) {
      navigate('/');
    }
  }, [hasCreatedOrganization]);

  return {
    fieldsMapper,
    commonSelectStyles,
    inputWrapperStyles,
    companyInformation,
    errors,
    fieldStates,
    onRegisterOrganization,
    onSelect,
    onChange,
    onSurveyAnswerDetailChange,
    setCompanyInformation,
    setErrors,
  };
};

export default useCompany;
